// Boot-time ports for the trials and upgrades tree. Wired in
// `src/composition/hosts/commerce/commerce-billing-ui-wiring.ts` before any lazy modal renders.
// Read members inside functions; never at module scope.
import type { ComponentType, Dispatch, ReactNode, SetStateAction } from 'react';

import type {
  TrackClinicianTrialEndedClick,
  TrackClinicianTrialEndedViewed,
  TrackEvidencePostUpgradeOnboardingClick,
  TrackEvidencePostUpgradeWelcomeClick,
  TrackNinetyDayTrialEndDiscreetClick,
  TrackNinetyDayTrialEndDiscreetViewed,
  TrackNinetyDayTrialEndingReminderClick,
  TrackNinetyDayTrialEndingReminderDismissed,
  TrackNinetyDayTrialEndingReminderViewed,
  TrackNinetyDayTrialRecapClick,
  TrackNinetyDayTrialRecapViewed,
  TrackPracticeInviteClick,
  TrackPracticeInviteViewed,
  TrackPracticeTrialClick,
  TrackPracticeTrialViewed,
  TrackTeamTrialUpgradeRequestSubmit,
  TrackTeamTrialUpgradeRequestViewed,
  TrackTrialUpgradeRequestCallbackClick,
  TrackTrialUpgradeRequestCallbackViewed,
} from '@heidi/commerce/contracts';
import type {
  components,
  ModalId,
  ModalViewAction,
  SalesBookingFormName,
  SubscriptionTier,
} from '@heidi/schema/ml-scribe';

import type { BillingFeature } from '../settings/billing/billing-ui-ports';

type SalesBookingRequest = components['schemas']['SalesBookingSubmissionRequest'];
type SalesBookingResponse = components['schemas']['SalesBookingSubmissionResponse'];

export type TrialRecapUsageStats = {
  answers_from_evidence?: number | null;
  sessions_with_evidence_queries?: number | null;
  references_used?: number | null;
};

export type TrialRecapUsageStatsResult = {
  userUsageStats: TrialRecapUsageStats | undefined;
  teamUsageStats: TrialRecapUsageStats | undefined;
  usageStats: TrialRecapUsageStats | undefined;
  isNoUsage: boolean;
  isLowUsage: boolean;
  shouldUseTeamStats: boolean;
  teamId: string | undefined;
  isClinicianPlan: boolean;
  isTeamAdmin: boolean | undefined;
  isUsageStatsPending: boolean;
  isUsageStatsSuccess: boolean;
};

export type TrialsQueuedModalCloseOptions = {
  action?: ModalViewAction;
  increment_count?: boolean;
  hide_forever?: boolean;
  skipPacing?: boolean;
};

export type CommerceTrialsUiPorts = {
  StackedSourceAvatars: ComponentType<{
    sources: Array<{ domain: string; displayName?: string }>;
    size?: 'sm' | 'md' | 'lg';
    maxVisible?: number;
    overflowVariant?: 'default' | 'pro';
    disableTooltips?: boolean;
    className?: string;
  }>;
  TrialStartModalBase: ComponentType<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    dismissible?: boolean;
    hideCloseIcon?: boolean;
    variant?: 'pro' | 'practice';
    headerIcon?: 'default' | 'gift';
    title: string;
    subtitle: ReactNode;
    features: BillingFeature[];
    primaryButtonText?: string;
    footerText?: string;
    hidePrimaryButtonIcon?: boolean;
    onPrimaryClick: () => void;
    secondaryButton?: ReactNode;
  }>;
  Loader: ComponentType<{ className?: string; size?: number }>;
  PhoneInput: ComponentType<{
    value?: string;
    onChange: (value?: string) => void;
    defaultCountry?: string;
    placeholder?: string;
    disabled?: boolean;
    countrySelectPopoverClassName?: string;
  }>;
  Head: ComponentType<{ children?: ReactNode }>;
  InputEmails: ComponentType<{
    value: string[];
    onChange: Dispatch<SetStateAction<string[]>>;
    limit?: number;
    failedEmailsData?: Record<string, string>;
    accessoryStart?: ReactNode;
    inputMode?: string;
    autoComplete?: string;
    'aria-invalid'?: boolean;
    'aria-label'?: string;
    placeholder?: string;
  }>;
  walkthroughPosterSrc: string;

  useEvidencePostUpgradeOnboardingModalOpen: () => [
    boolean,
    (update: boolean | ((prev: boolean) => boolean)) => void,
  ];
  useEvidencePostUpgradeWelcomeDeferQueue: () => [
    boolean,
    (update: boolean | ((prev: boolean) => boolean)) => void,
  ];
  useEnqueueModal: () => (modal: {
    id: ModalId;
    priority: number;
    dismissible?: boolean;
  }) => void;
  useDequeueModal: () => (id: ModalId) => void;
  useSeenModalIds: () => [
    Set<ModalId> | null,
    (
      update: Set<ModalId> | null | ((prev: Set<ModalId> | null) => Set<ModalId> | null)
    ) => void,
  ];
  useQueuedModal: (modalId: ModalId) => {
    isOpen: boolean;
    dismissible: boolean;
    handleClose: () => void;
    onOpenChange: (open: boolean) => void;
    closeWithOptions: (options?: TrialsQueuedModalCloseOptions) => void;
  };
  useInviteTeamStepCompleted: () => boolean;
  useSetPracticeTrialModalPending: () => (pending: boolean) => void;

  useEvidenceAccess: () => { isInSessionEvidenceShown: boolean };
  useQuerySourceRecommendations: (enabled?: boolean) => {
    data?: { sources?: Array<{ domain: string }> };
  };
  useShowSessionsListAssistantView: () => boolean;
  useTrialRecapUsageStats: (enabled: boolean) => TrialRecapUsageStatsResult;
  usePracticeInvite: () => {
    sendInvites: (
      validEmails: string[]
    ) => Promise<{ successCount: number; failedEmails: string[] } | null>;
    isPending: boolean;
  };
  usePracticeInviteEmailForm: (invalidMessage: string) => {
    emails: string[];
    errorMessage: string | null;
    failedEmailsData: Record<string, string>;
    onEmailsChange: Dispatch<SetStateAction<string[]>>;
    validateEmails: () => Promise<string[] | null>;
    setFailedEmails: (failedEmails: string[], message?: string) => void;
    resetEmailForm: () => void;
  };
  useQueryUserTier: () => {
    userTier: SubscriptionTier | null | undefined;
  };
  useMutateMarkModalAsViewed: () => {
    mutate: (variables: {
      modal_id: ModalId;
      increment_count?: boolean;
      action?: ModalViewAction;
      hide_forever?: boolean | null;
    }) => void;
  };
  useMutateSubmitSalesBookingForm: () => {
    mutateAsync: (variables: {
      formName: SalesBookingFormName;
      body: SalesBookingRequest;
    }) => Promise<SalesBookingResponse>;
    isPending: boolean;
  };

  isPracticeInviteFlowEnabled: () => boolean;
  getPrivacyPolicyLink: (country?: string, locale?: string) => string;
  getHubSpotTrackingCookie: () => string | undefined;
  mapOrganisationSizeToHubSpot: (size: string | null | undefined) => string | undefined;
  normalizeCountryName: (country: string | null | undefined) => string | null;

  analytics: {
    trackPracticeTrialClick: TrackPracticeTrialClick;
    trackPracticeTrialViewed: TrackPracticeTrialViewed;
    trackPracticeInviteClick: TrackPracticeInviteClick;
    trackPracticeInviteViewed: TrackPracticeInviteViewed;
    trackTeamTrialUpgradeRequestViewed: TrackTeamTrialUpgradeRequestViewed;
    trackNinetyDayTrialRecapTeamUpgradeRequestSubmit: TrackTeamTrialUpgradeRequestSubmit;
    trackClinicianTrialEndedTeamUpgradeRequestSubmit: TrackTeamTrialUpgradeRequestSubmit;
    trackPracticeTrialEndedTeamUpgradeRequestSubmit: TrackTeamTrialUpgradeRequestSubmit;
    trackNinetyDayTrialEndDiscreetClick: TrackNinetyDayTrialEndDiscreetClick;
    trackNinetyDayTrialEndDiscreetViewed: TrackNinetyDayTrialEndDiscreetViewed;
    trackNinetyDayTrialEndingReminderClick: TrackNinetyDayTrialEndingReminderClick;
    trackNinetyDayTrialEndingReminderDismissed: TrackNinetyDayTrialEndingReminderDismissed;
    trackNinetyDayTrialEndingReminderViewed: TrackNinetyDayTrialEndingReminderViewed;
    trackNinetyDayTrialRecapClick: TrackNinetyDayTrialRecapClick;
    trackNinetyDayTrialRecapViewed: TrackNinetyDayTrialRecapViewed;
    trackTrialUpgradeRequestCallbackClick: TrackTrialUpgradeRequestCallbackClick;
    trackTrialUpgradeRequestCallbackViewed: TrackTrialUpgradeRequestCallbackViewed;
    trackClinicianTrialEndedClick: TrackClinicianTrialEndedClick;
    trackClinicianTrialEndedViewed: TrackClinicianTrialEndedViewed;
    trackEvidencePostUpgradeOnboardingClick: TrackEvidencePostUpgradeOnboardingClick;
    trackEvidencePostUpgradeWelcomeClick: TrackEvidencePostUpgradeWelcomeClick;
  };
};

let ports: CommerceTrialsUiPorts | null = null;

export const configureCommerceTrialsUi = (next: CommerceTrialsUiPorts): void => {
  ports = next;
};

export const getCommerceTrialsUiPorts = (): CommerceTrialsUiPorts => {
  if (!ports) {
    throw new Error(
      '[commerce/ui/trials-and-upgrades] not configured — call configureCommerceTrialsUi() before rendering trials UI.'
    );
  }
  return ports;
};

/** Test-only: drops the registered ports so a suite can assert the failure. */
export const __resetCommerceTrialsUiPortsForTests = (): void => {
  ports = null;
};
